import { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
    DataGrid,
    Column,
    FilterRow,
    HeaderFilter,
    LoadPanel,
    Paging,
    Scrolling,
    SearchPanel,
    Toolbar,
    Item,
} from "devextreme-react/data-grid";

import { dataGridConfig } from "../../../constants";
import { calculateGridHeight, convertFormatDate } from "../../../util/Utils";
import { rollOverClientApi } from "../../../api-client-nswag/taxportal-client-runtime";
import "./ExecutionDetailsModal.scss";

interface ExecutionDetailsModalProps {
    open: boolean;
    onClose: () => void;
    rolloverId?: number;
}
type TabValue = "retrieval" | "upload";

const TABS: { label: string; value: TabValue }[] = [
    { label: "Retrieval", value: "retrieval" },
    { label: "Upload to site", value: "upload" },
];

const getStatusClass = (status: string): string => {
    if (!status) return "execution-details-modal__status--default";
    const lower = status.toLowerCase();
    if (lower.startsWith("successful")) return "execution-details-modal__status--successful";
    if (lower.startsWith("error")) return "execution-details-modal__status--error";
    return "execution-details-modal__status--default";
};

export const ExecutionDetailsModal = ({
    open,
    onClose,
    rolloverId,
}: ExecutionDetailsModalProps) => {
    const [activeTab, setActiveTab] = useState<TabValue>("retrieval");
    const [retrievalRows, setRetrievalRows] = useState<any[]>([]);
    const [retrievalPending, setRetrievalPending] = useState(false);
    const [uploadRows, setUploadRows] = useState<any[]>([]);
    const [uploadPending, setUploadPending] = useState(false);

    useEffect(() => {
        if (!open || rolloverId === undefined) return;
        setRetrievalRows([]);
        setUploadRows([]);
        setRetrievalPending(true);
        setUploadPending(true);

        Promise.allSettled([
            rollOverClientApi.getRetrievalDetailList(rolloverId),
            rollOverClientApi.getRetrievalUploadList(rolloverId),
        ]).then(([retrievalResult, uploadResult]) => {
            if (retrievalResult.status === "fulfilled") {
                const mapped = retrievalResult.value.map((item) => ({
                    id: item.rolloverRetrivalDetailId,
                    date: item.created,
                    initiatedBy: item.createdBy,
                    process: item.process,
                    status: item.status,
                }));
                setRetrievalRows(mapped);
            } else {
                console.error("Error fetching retrieval detail list:", retrievalResult.reason);
                setRetrievalRows([]);
            }

            if (uploadResult.status === "fulfilled") {
                const mapped = uploadResult.value.map((item, index) => ({
                    id: index + 1,
                    dateRestored: item.dateRestore,
                    initiatedBy: item.initiatedBy,
                    filename: item.fileName,
                    folderParent: item.folderName,
                    size: item.fileSize,
                    zip: item.zipName,
                    restoreStatus: item.restoreStatus,
                }));
                setUploadRows(mapped);
            } else {
                console.error("Error fetching retrieval upload list:", uploadResult.reason);
                setUploadRows([]);
            }

            setRetrievalPending(false);
            setUploadPending(false);
        });
    }, [open, rolloverId]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            className="hq-modal cui-c_modal execution-details-modal"
        >
            <DialogTitle className="cui-c_modal-title hq-title">
                Execution Details
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    className="modal_button-cancel"
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent className="cui-c_modal-body">
                <div className="execution-details-modal__section">
                    {activeTab === "retrieval" && (
                        <div className="execution-details-modal__grid-container">
                            <DataGrid
                                height={calculateGridHeight(420)}
                                {...dataGridConfig}
                                dataSource={retrievalRows}
                                noDataText={retrievalPending ? "Loading..." : "No Retrieval data"}
                                wordWrapEnabled
                                rowAlternationEnabled
                            >
                                <LoadPanel enabled={false} />
                                <Scrolling mode="standard" />
                                <FilterRow visible />
                                <HeaderFilter visible />
                                <Paging enabled defaultPageSize={10} />
                                <Toolbar>
                                    <Item location="before">
                                        <div className="execution-details-modal__tab-group">
                                            {TABS.map((tab) => (
                                                <Button
                                                    key={tab.value}
                                                    variant={activeTab === tab.value ? "contained" : "outlined"}
                                                    size="small"
                                                    onClick={() => setActiveTab(tab.value)}
                                                    className="execution-details-modal__tab-btn"
                                                >
                                                    {tab.label}
                                                </Button>
                                            ))}
                                        </div>
                                    </Item>
                                </Toolbar>
                                <Column dataField="date" caption="Date" minWidth={120} alignment="left" 
                                cellRender={(e) => <>{convertFormatDate(e.data.date, "MM/DD/YYYY")}</>} />
                                <Column dataField="initiatedBy" caption="Initiated by" minWidth={180} alignment="left" />
                                <Column dataField="process" caption="Process" minWidth={200} alignment="left" />
                                <Column
                                    dataField="status"
                                    caption="Status"
                                    minWidth={160}
                                    alignment="left"
                                    cellRender={({ value }: { value: string }) => (
                                        <span className={getStatusClass(value)}>{value}</span>
                                    )}
                                />
                            </DataGrid>
                        </div>
                    )}

                    {activeTab === "upload" && (
                        <div className="execution-details-modal__grid-container">
                            <DataGrid
                                height={calculateGridHeight(420)}
                                {...dataGridConfig}
                                dataSource={uploadRows}
                                noDataText={uploadPending ? "Loading..." : "No Upload to site data"}
                                wordWrapEnabled
                                rowAlternationEnabled
                            >
                                <LoadPanel enabled={false} />
                                <Scrolling mode="standard" />
                                <FilterRow visible />
                                <HeaderFilter visible />
                                <Paging enabled defaultPageSize={10} />
                                <SearchPanel visible placeholder="Search by file name..." />
                                <Toolbar>
                                    <Item location="before">
                                        <div className="execution-details-modal__tab-group">
                                            {TABS.map((tab) => (
                                                <Button
                                                    key={tab.value}
                                                    variant={activeTab === tab.value ? "contained" : "outlined"}
                                                    size="small"
                                                    onClick={() => setActiveTab(tab.value)}
                                                    className="execution-details-modal__tab-btn"
                                                >
                                                    {tab.label}
                                                </Button>
                                            ))}
                                        </div>
                                    </Item>
                                    <Item name="searchPanel" location="after" />
                                </Toolbar>
                                <Column dataField="dateRestored" caption="Date Restored" minWidth={130} alignment="left"
                                cellRender={(e) => <>{convertFormatDate(e.data.dateRestored, "MM/DD/YYYY")}</>} />
                                <Column dataField="initiatedBy" caption="Initiated by" minWidth={180} alignment="left" />
                                <Column dataField="filename" caption="Filename" minWidth={100} alignment="left" />
                                <Column dataField="folderParent" caption="Folder / Parent Folder" minWidth={220} alignment="left" />
                                <Column dataField="size" caption="Size" minWidth={80} width={80} alignment="left" />
                                <Column dataField="zip" caption="Zip" minWidth={80} width={80} alignment="left" />
                                <Column
                                    dataField="restoreStatus"
                                    caption="Restore status"
                                    minWidth={180}
                                    alignment="left"
                                    cellRender={({ value }: { value: string }) => (
                                        <span className={getStatusClass(value)}>{value}</span>
                                    )}
                                />
                            </DataGrid>
                        </div>
                    )}
                </div>
            </DialogContent>
            <DialogActions className="cui-c_modal-footer">
                <Button onClick={onClose} className="hq_button hq_button-secondary">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};
